import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ManualPaymentDialog } from "./ManualPaymentDialog";
import { useCollection } from "@/hooks/useCollection";
import { inr, text, fmtTs } from "@/lib/format";

const TYPE = { start_project: "Project fee (credit)", top_up: "Top-up (credit)", wallet_used: "Used (debit)", wallet_restored: "Restored (credit)" };

const Txns = ({ cid }) => {
  const tx = useCollection(`customer_payments/${cid}/transactions`);
  const rows = [...tx].sort((a, b) => String(b.created_at?.seconds ?? b.created_at).localeCompare(String(a.created_at?.seconds ?? a.created_at)));
  return (
    <Table>
      <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Type</TableHead><TableHead>For</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.map((t) => (
          <TableRow key={t.id} data-testid={`wallet-tx-${t.id}`}>
            <TableCell className="text-xs text-muted-foreground">{fmtTs(t.created_at)}</TableCell>
            <TableCell className={`text-xs ${t.type === "wallet_used" ? "text-red-600" : "text-emerald-600"}`}>{TYPE[t.type] || text(t.type)}</TableCell>
            <TableCell className="text-xs text-muted-foreground">{text(t.purpose)}<span className="block font-mono text-[10px]">{text(t.ref_id, "")}</span></TableCell>
            <TableCell className="font-mono text-sm">{t.type === "wallet_used" ? "−" : "+"}{inr(t.amount)}</TableCell>
            <TableCell><StatusBadge status={t.status || (t.payment_received === "yes" ? "paid" : "pending")} /></TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No transactions.</TableCell></TableRow>}
      </TableBody>
    </Table>
  );
};

export const WalletsTab = ({ userBy }) => {
  const wallets = useCollection("customer_payments");
  const [open, setOpen] = useState(null);
  const [manual, setManual] = useState(null);
  const name = (id) => { const u = userBy[id] || {}; return u.name || u.full_name || id; };
  return (
    <Card>
      <div className="overflow-x-auto"><Table>
        <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Wallet balance</TableHead><TableHead>Project fees paid</TableHead><TableHead>Top-ups</TableHead><TableHead>Project-fee spend</TableHead><TableHead>Raw-material spend</TableHead><TableHead>Updated</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {wallets.map((w) => (
            <TableRow key={w.id} className="cursor-pointer" onClick={() => setOpen(w.id)} data-testid={`wallet-row-${w.id}`}>
              <TableCell className="text-sm">{text(name(w.id))}<span className="block font-mono text-[10px] text-muted-foreground">{w.id}</span></TableCell>
              <TableCell className="font-mono font-semibold">{inr(w.wallet_balance)}</TableCell>
              <TableCell className="font-mono text-sm">{inr(w.startaproj_amt)}</TableCell>
              <TableCell className="font-mono text-sm">{inr(w.topup_amt ?? w.top_up_amt)}</TableCell>
              <TableCell className="font-mono text-sm">{inr(w.startaproj_used_amt)}</TableCell>
              <TableCell className="font-mono text-sm">{inr(w.rawmaterial_used_amt)}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{fmtTs(w.updated_at)}</TableCell>
              <TableCell><Button size="sm" variant="ghost" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setManual({ target: "wallet_topup", customer_id: w.id, label: `Record top-up for ${name(w.id)}` }); }} data-testid={`wallet-topup-${w.id}`}>Record top-up</Button></TableCell>
            </TableRow>
          ))}
          {wallets.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No wallets yet.</TableCell></TableRow>}
        </TableBody>
      </Table></div>
      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="wallet-drawer">
          {open && <>
            <SheetHeader className="text-left"><SheetTitle className="font-heading">{text(name(open))} · wallet</SheetTitle><SheetDescription className="font-mono">customer_payments/{open}/transactions</SheetDescription></SheetHeader>
            <div className="mt-4"><Txns cid={open} /></div>
          </>}
        </SheetContent>
      </Sheet>
      <ManualPaymentDialog ctx={manual} onClose={() => setManual(null)} />
    </Card>
  );
};
